import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SiteStatusProvider, useSiteStatus } from "@/contexts/SiteStatusContext";
import MaintenanceSplash from "@/components/app/MaintenanceSplash";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SearchPage from "@/pages/Search";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Profile from "@/pages/Profile";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CookieConsent from "@/components/app/CookieConsent";
import AdminPausedBanner from "@/components/app/AdminPausedBanner";

// Detect the Google-auth #session_id fragment synchronously during render
// (NOT in useEffect — that's too late and causes race conditions).
function AppRouter() {
  const location = useLocation();
  const { paused, loading: siteLoading } = useSiteStatus();
  const { user, loading: authLoading } = useAuth();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // While we don't know the site status OR who's logged in yet, don't show
  // the splash — it would flash on every refresh for the admin too.
  const isAdmin = !!user?.is_admin;
  if (paused && !siteLoading && !authLoading && !isAdmin) {
    return <MaintenanceSplash />;
  }
  return (
    <>
      <AdminPausedBanner />
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <SearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <Pricing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteStatusProvider>
          <LanguageProvider>
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#121212",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  fontFamily: "IBM Plex Sans, system-ui, sans-serif",
                  fontSize: 13,
                },
              }}
            />
            <AppRouter />
            <CookieConsent />
          </LanguageProvider>
        </SiteStatusProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
